import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../api/client';
import {
  FolderOpenIcon, ArrowTopRightOnSquareIcon, DocumentIcon,
  PencilSquareIcon, ArrowUpTrayIcon, XMarkIcon,
  CheckBadgeIcon, NoSymbolIcon,
} from '@heroicons/react/24/outline';
import { DATE_FLOOR, completionMax, expiryMax } from '../lib/dateBounds';

// Categories the staff-side upload can write to. Matches the backend's
// VALID_DOC_CATEGORIES list (less the management-only ones like
// hire_letter / term_letter / pay_change which staff shouldn't add).
const SELF_UPLOAD_CATEGORIES = [
  { value: 'training_cert',  label: 'Training certificate' },
  { value: 'compliance_doc', label: 'Compliance document' },
  { value: 'background_check', label: 'Background check result' },
  { value: 'performance_doc', label: 'Performance document' },
  { value: 'other',          label: 'Other' },
];

// Reusable employee-file view. Renders the unified personnel-file data
// from either /api/my/file (staff side) or /api/staff/:id/file (admin
// side, see myFileController.buildFileForUser). Strictly presentational —
// no fetching, no mutations — so the same UI works for both surfaces.
//
// Props:
//   data        — the response shape from buildFileForUser
//   embedded    — true when rendered inside another page (drops the
//                 page-level header so it slots in cleanly under tabs)

const SOURCE_ICON = {
  offer_letter: DocumentIcon,
  handbook_ack: PencilSquareIcon,
  onboarding: DocumentIcon,
  staff_document: DocumentIcon,
};

const STATUS_META = {
  incomplete: { label: 'Not submitted', dot: 'bg-red-500', pill: 'bg-red-50 text-red-700 border-red-200', border: 'border-l-red-400' },
  submitted:  { label: 'Submitted',     dot: 'bg-yellow-500', pill: 'bg-yellow-50 text-yellow-700 border-yellow-200', border: 'border-l-yellow-400' },
  verified:   { label: 'Verified',      dot: 'bg-green-500', pill: 'bg-green-50 text-green-700 border-green-200', border: 'border-l-green-400' },
  expired:    { label: 'Expired',       dot: 'bg-red-600', pill: 'bg-red-100 text-red-800 border-red-300', border: 'border-l-red-600' },
  // `on_file` is what a staff_documents row is: a document we hold, not a
  // requirement anybody has satisfied. It used to render as 'Verified' — see
  // the ruling at myFileController.js. Neutral grey deliberately: it is not a
  // step on the red/yellow/green traffic light, because it is not a step.
  on_file:    { label: 'On file',       dot: 'bg-gray-400', pill: 'bg-gray-50 text-gray-600 border-gray-200', border: 'border-l-gray-300' },
};

// 087 — how a document_kind reads on screen. Education is ANY-ONE-OF: one of
// these satisfies the requirement outright, because holding the higher one
// entails the lower (Ramal Taylor, 2026-09-12, migration 076).
//
// A requirement whose allowed_document_kinds is null does not distinguish
// kinds and shows no picker and no label.
const DOCUMENT_KIND_LABELS = {
  hs_diploma:          'High school diploma',
  ged:                 'GED',
  college_transcript:  'College transcript',
  degree:              'Degree',
  college_certificate: 'College certificate',
  cda:                 'CDA',
};
const kindLabel = (k) => DOCUMENT_KIND_LABELS[k] || k;


// ─── POLICY ACKNOWLEDGMENTS ON THE FILE ─────────────────────────────
//
// THE FILE IS THE RECORD. /my/policies is the TASK — what you owe and by
// when. Both read the same builder, so there is no second derivation to drift.
//
// Shows WHAT THEY AGREED TO: the version pinned at signing, the date and
// time, every covered document with ITS version. Openable AT THE VERSION
// SIGNED, not merely the current one — an acknowledgment saying "I have read
// this" is worthless if the thing can never be seen again.
//
// A newer version is shown ALONGSIDE, never instead. Same five-state
// vocabulary as /my/policies, reused rather than rewritten.
function AcknowledgmentsSection({ acknowledgments }) {
  const [busy, setBusy] = useState(null);

  // Two steps, not a navigation: an <a href> to the API carries no
  // Authorization header. Fetch the bytes through the authenticated call,
  // hand the frame a blob: URL. Same reason as MyPoliciesPage.
  const open = async (versionId) => {
    if (busy) return;
    setBusy(versionId);
    try {
      const { data } = await api.get(`/policies/versions/${versionId}/view`, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(
        data.type ? data : new Blob([data], { type: 'application/pdf' })
      );
      window.open(blobUrl, '_blank');
      // Revoked on a timer rather than immediately: the new tab needs the url
      // to still resolve when it loads.
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    } catch (err) {
      let msg = 'Could not open that document.';
      try {
        const text = err?.response?.data instanceof Blob ? await err.response.data.text() : null;
        if (text) msg = JSON.parse(text).error || msg;
      } catch { /* keep the default */ }
      toast.error(msg);
    } finally { setBusy(null); }
  };

  if (!acknowledgments || acknowledgments.length === 0) return null;

  return (
    <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
      <div className="px-5 py-3 border-b border-gray-100 bg-gray-50/50">
        <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
          Policy Acknowledgments
        </h2>
        <p className="text-xs text-gray-500 mt-0.5">
          The version acknowledged stays on this file permanently.
        </p>
      </div>
      <ul className="divide-y divide-gray-50">
        {acknowledgments.map((a) => (
          <li key={a.key} className="px-5 py-3">
            <div className="flex items-start gap-2 flex-wrap">
              <p className="text-sm font-medium text-gray-900">{a.title}</p>
              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${
                a.status === 'current'
                  ? 'bg-green-50 text-green-700 border-green-200'
                  : 'bg-amber-50 text-amber-700 border-amber-200'}`}>
                {a.status === 'current' ? 'Acknowledged' : 'Newer version'}
              </span>
            </div>
            {/* The DATE AND TIME, not merely "acknowledged". */}
            <p className="text-xs text-gray-600 mt-0.5">
              Signed “{a.signature_text}” on{' '}
              {new Date(a.acknowledged_at).toLocaleString('en-US', {
                year: 'numeric', month: 'short', day: 'numeric',
                hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York',
              })} ET
            </p>
            <ul className="mt-1.5 space-y-1">
              {a.documents.map((d) => (
                <li key={d.document_id} className="text-xs text-gray-600">
                  <button type="button" disabled={busy === d.acknowledged_version_id}
                    onClick={() => open(d.acknowledged_version_id)}
                    className="text-orange-700 hover:underline disabled:opacity-50 text-left">
                    {d.title} — {d.acknowledged_version_label}
                  </button>
                  {/* ALONGSIDE, never instead. */}
                  {d.state === 'superseded' && (
                    <span className="text-amber-700">
                      {' '}· newer version {d.current_version_label} published{' '}
                      {d.current_published_at
                        ? new Date(d.current_published_at).toLocaleDateString('en-US',
                            { year: 'numeric', month: 'short', day: 'numeric' })
                        : ''}, not yet acknowledged
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function EmployeeFile({
  data, embedded = false, canUpload = false, uploadTargetId, onUploaded,
  // Admin per-row upload: when set, each onboarding row that hasn't
  // been verified yet gets an inline "Upload" button so admin can
  // satisfy that exact step without leaving the page. Most commonly
  // the candidate/staff's own user id.
  adminManageUserId = null,
  // Director-only. Surfaces the collapsed "Correct dates" control on rows that
  // are ALREADY VERIFIED. Deliberately NOT derived from adminManageUserId:
  // admins manage files, and rewriting a verified date is a director act.
  canCorrectVerifiedDates = false,
}) {
  if (!data) return null;
  const { employee, categories, total_items, acknowledgments } = data;
  const [uploadOpen, setUploadOpen] = useState(false);
  // Holds the onboarding row currently being uploaded against by admin.
  // null = modal closed. Object = the EmployeeFile item we're acting on.
  const [adminRowTarget, setAdminRowTarget] = useState(null);

  return (
    <div className={embedded ? '' : 'animate-slide-up max-w-3xl mx-auto'}>
      {!embedded && (
        <div className="mb-6 flex items-start gap-4">
          <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
            <FolderOpenIcon className="w-6 h-6 text-orange-600" />
          </div>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-gray-900">My Employment File</h1>
            <p className="text-sm text-gray-500 mt-0.5">
              Everything in your personnel file at Bright Beginnings.
              {total_items > 0 && (
                <> {total_items} document{total_items === 1 ? '' : 's'}.</>
              )}
            </p>
            <p className="text-xs text-gray-400 mt-1">
              Items can't be removed — contact your director if something needs to come out.
            </p>
          </div>
          {canUpload && (
            <button onClick={() => setUploadOpen(true)}
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-white bg-orange-500 hover:bg-orange-600 px-4 py-2 rounded-xl flex-shrink-0">
              <ArrowUpTrayIcon className="w-4 h-4" /> Upload
            </button>
          )}
        </div>
      )}

      {/* An acknowledgment is a file item too: somebody with no uploaded
          documents but a signed policy does NOT have an empty file. */}
      {categories.length === 0 && (!acknowledgments || acknowledgments.length === 0) ? (
        <div className="bg-white border border-gray-200 rounded-2xl p-10 text-center">
          <FolderOpenIcon className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-600 font-medium">File is empty for now.</p>
          <p className="text-sm text-gray-500 mt-1">
            Documents appear here as onboarding steps complete and as admins upload them.
          </p>
        </div>
      ) : (
        <div className="space-y-5">
          <AcknowledgmentsSection acknowledgments={acknowledgments} />
          {categories.map((group) => (
            <div key={group.key} className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
              <div className="px-5 py-3 border-b border-gray-100 bg-gray-50/50">
                <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">{group.label}</h2>
                <p className="text-xs text-gray-500 mt-0.5">{group.items.length} item{group.items.length === 1 ? '' : 's'}</p>
              </div>
              <ul className="divide-y divide-gray-100">
                {group.items.map((item) => {
                  const Icon = SOURCE_ICON[item.source] || DocumentIcon;
                  const hasFile = !!item.url;
                  const meta = STATUS_META[item.status_key] || STATUS_META.incomplete;
                  const expFlag = item.expiration_flag;
                  return (
                    <li key={item.id}
                      className={`px-5 py-3 flex items-start gap-3 hover:bg-gray-50/50 border-l-4 ${meta.border}`}>
                      <div className={`w-2.5 h-2.5 rounded-full ${meta.dot} flex-shrink-0 mt-2`} aria-hidden />
                      <div className="w-7 h-7 rounded-lg bg-orange-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Icon className="w-3.5 h-3.5 text-orange-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-sm font-medium text-gray-900">{item.title}</p>
                          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${meta.pill}`}>
                            {meta.label}
                          </span>
                          {/* WHICH qualifying document this is. Absent is
                              silent on purpose: NULL means "satisfied, kind
                              not recorded", never unsatisfied, so it must not
                              render as a gap the reader tries to close. */}
                          {item.document_kind && (
                            <span className="text-[10px] font-medium px-2 py-0.5 rounded-full border bg-slate-50 text-slate-600 border-slate-200">
                              {kindLabel(item.document_kind)}
                            </span>
                          )}
                          {expFlag?.kind === 'expiring_soon' && (
                            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full border bg-amber-50 text-amber-700 border-amber-200">
                              Expires in {expFlag.days_remaining}d
                            </span>
                          )}
                        </div>
                        {item.note && (
                          <p className="text-xs text-gray-500 mt-0.5">{item.note}</p>
                        )}
                        {item.dated_at && (
                          <p className="text-[11px] text-gray-400 mt-0.5">
                            {new Date(item.dated_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                          </p>
                        )}
                        {/* Verified items that never had a file attached —
                            legacy manual-verify or bypass path. Tell the
                            director why View is missing rather than
                            leaving it silent. */}
                        {!hasFile && item.status_key === 'verified' && !item.form_url && (
                          <p className="text-[11px] text-amber-700 mt-0.5 italic">
                            Marked verified without a file on record — upload one below to attach.
                          </p>
                        )}
                      </div>
                      {hasFile && (
                        <a href={item.url} target="_blank" rel="noreferrer"
                          className="text-xs font-medium text-orange-600 hover:text-orange-700 inline-flex items-center gap-1 flex-shrink-0">
                          <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" /> View
                        </a>
                      )}
                      {/* Fillable forms (W-4, Direct Deposit) — staff
                          completes in-portal. CTA only shows when the
                          item isn't already verified. */}
                      {!hasFile && item.form_url && item.status_key !== 'verified' && (
                        <Link to={item.form_url}
                          className="text-xs font-semibold text-white bg-orange-500 hover:bg-orange-600 inline-flex items-center gap-1 flex-shrink-0 px-3 py-1.5 rounded-lg">
                          <PencilSquareIcon className="w-3.5 h-3.5" /> Fill out
                        </Link>
                      )}
                      {/* Per-row upload. Shows on every onboarding item
                          (even verified ones) so a staff member or admin
                          can re-upload — e.g. CPR cert renewal needs a
                          new doc + new expiration date. Hidden on
                          fillable forms (W-4, DD, Handbook) — those go
                          through Fill out. Backend uses the role
                          hierarchy to decide whether the re-upload
                          auto-verifies or needs supervisor approval. */}
                      {adminManageUserId && item.source === 'onboarding'
                        && item.template_id
                        && !item.form_url && (
                        <button onClick={() => setAdminRowTarget(item)}
                          className="text-xs font-semibold text-orange-600 hover:text-orange-700 inline-flex items-center gap-1 flex-shrink-0 px-3 py-1.5 rounded-lg border border-orange-200 hover:bg-orange-50">
                          <ArrowUpTrayIcon className="w-3.5 h-3.5" />
                          {hasFile ? 'Replace' : 'Upload'}
                        </button>
                      )}
                      {/* Admin Verify — surfaces only on onboarding items
                          that a staff member uploaded (status_key
                          'submitted') and that the admin can decide on. */}
                      {adminManageUserId && item.source === 'onboarding'
                        && item.source_id
                        && item.status_key === 'submitted' && (
                        <AdminVerifyButton
                          userId={adminManageUserId} item={item}
                          onChanged={onUploaded}
                        />
                      )}
                      {/* CORRECT DATES — director only, and only once a row
                          is already verified.

                          SECONDARY, NOT PRIMARY. A verified row's default
                          appearance is a green tick; this is a small link that
                          opens the fields when somebody needs them, and is not
                          inviting when they do not. Ruled 2026-09-12. */}
                      {canCorrectVerifiedDates && item.source === 'onboarding'
                        && item.source_id
                        && item.status_key === 'verified' && (
                        <CorrectDatesControl
                          userId={adminManageUserId} item={item} onChanged={onUploaded}
                        />
                      )}
                      {/* Admin Reject — same surface as Verify, opens a
                          modal that requires a reason and wipes the
                          upload so staff can re-submit. */}
                      {adminManageUserId && item.source === 'onboarding'
                        && item.source_id
                        && item.status_key === 'submitted' && (
                        <AdminRejectButton
                          userId={adminManageUserId} item={item}
                          onChanged={onUploaded}
                        />
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      )}

      {/* Embedded upload CTA — sits below the list when EmployeeFile is
          dropped inside another page (admin staff File tab, candidate
          Employment File tab). The unframed version (My Employment
          File page) uses the header button instead. */}
      {canUpload && embedded && (
        <div className="mt-3 text-right">
          <button onClick={() => setUploadOpen(true)}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-orange-500 hover:bg-orange-600 px-3 py-1.5 rounded-lg">
            <ArrowUpTrayIcon className="w-3.5 h-3.5" /> Upload to file
          </button>
        </div>
      )}

      {!embedded && (
        <p className="text-xs text-gray-400 text-center mt-6">
          Employee: <strong>{employee.first_name} {employee.last_name}</strong>
          {employee.role && <> · {employee.role}</>}
        </p>
      )}

      {uploadOpen && (
        <UploadModal
            ownerName={[employee?.first_name, employee?.last_name].filter(Boolean).join(' ')}
          targetId={uploadTargetId}
          onCancel={() => setUploadOpen(false)}
          onUploaded={() => { setUploadOpen(false); onUploaded && onUploaded(); }}
        />
      )}

      {adminRowTarget && (
        <AdminRowUploadModal
            ownerName={[employee?.first_name, employee?.last_name].filter(Boolean).join(' ')}
          userId={adminManageUserId}
          item={adminRowTarget}
          onCancel={() => setAdminRowTarget(null)}
          onUploaded={() => { setAdminRowTarget(null); onUploaded && onUploaded(); }}
        />
      )}
    </div>
  );
}

// Admin one-click verify on a staff-submitted onboarding item. Calls
// the unified file controller (Phase 1 consolidation) which writes an
// audit log row and recomputes the user's status (pending_hire →
// onboarding → active transitions, all there).
// ─── Correct dates on an already-verified item ──────────────────────
//
// The gap this closes, found 2026-09-12: a date could be corrected while a row
// sat in Pending Approvals, and could not be corrected once it was verified.
// The same hole, one state later. Marsha Thornton's Drug Test row was the first
// row anybody needed it for — stored 2026-03-23, the certificate's date of test
// is 2026-03-19.
//
// THREE THINGS THIS DOES DIFFERENTLY FROM THE VERIFICATION-TIME EDIT:
//
//   1. DIRECTOR ONLY, enforced server-side as well (fileController returns 403
//      for anyone else). The parent only decides whether to render it.
//   2. A REASON IS MANDATORY and is stored in the audit row alongside the
//      before and after values. Correcting a date during verification is part
//      of verifying; changing one afterwards overrides a previous decision, and
//      somebody will ask why.
//   3. It is COLLAPSED. A verified row still looks like a verified row.
// Hoisted to module scope: AdminVerifyButton needs it too, and a helper
// defined inside one component is invisible to the next one.
const dateOnly = (v) => (v ? String(v).slice(0, 10) : '');

function CorrectDatesControl({ userId, item, onChanged }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [completion, setCompletion] = useState(dateOnly(item.external_completion_date));
  const [expiry, setExpiry] = useState(dateOnly(item.expiration_date));
  const [reason, setReason] = useState('');

  const storedCompletion = dateOnly(item.external_completion_date);
  const storedExpiry = dateOnly(item.expiration_date);
  const changed = completion !== storedCompletion || expiry !== storedExpiry;

  const save = async () => {
    if (!changed) return toast.error('Nothing changed.');
    if (!reason.trim()) return toast.error('A reason is required — it is stored with the change.');
    setBusy(true);
    try {
      await api.post(`/file/${userId}/items/onboarding_progress/${item.source_id}/verify`, {
        external_completion_date: completion || null,
        expiration_date: expiry || null,
        correction_reason: reason.trim(),
      });
      toast.success(`Date corrected on ${item.title}. The change is recorded against your name.`);
      setOpen(false);
      setReason('');
      onChanged && onChanged();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not save the correction.');
    } finally { setBusy(false); }
  };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)}
        className="text-[11px] text-gray-400 hover:text-orange-600 underline underline-offset-2 flex-shrink-0 self-center">
        Correct dates
      </button>
    );
  }

  return (
    <div className="w-full mt-2 rounded-lg border border-amber-200 bg-amber-50/60 px-3 py-2.5">
      <div className="flex items-start justify-between gap-2 mb-2">
        {/* NAME THE REQUIREMENT. A file carries 21 rows and this panel opens
            inline under one of them; "Correct a verified date" was true of
            every row on the page. The director is overriding a previous
            decision and the screen has to say which one, before the write
            and not only in the audit row afterwards. */}
        <p className="text-[11px] font-semibold uppercase tracking-wide text-amber-800 min-w-0 break-words">
          Correct a verified date — {item.title}
        </p>
        <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-gray-600">
          <XMarkIcon className="w-4 h-4" />
        </button>
      </div>
      <p className="text-[11px] text-amber-900 mb-2 leading-relaxed">
        <span className="font-semibold">{item.title}</span> is already verified.
        Changing a date here overrides that decision, so the reason is stored
        with the before and after values.
      </p>
      <div className="flex flex-wrap gap-3">
        {(item.requires_completion_date || storedCompletion) && (
          <label className="block">
            <span className="block text-[11px] text-gray-600 mb-1">Completion date</span>
            <input type="date" min={DATE_FLOOR} max={completionMax()} value={completion} onChange={(e) => setCompletion(e.target.value)}
              className="bg-white border border-gray-300 rounded-lg px-2 py-1 text-xs" />
          </label>
        )}
        {(item.requires_expiration_date || storedExpiry) && (
          <label className="block">
            <span className="block text-[11px] text-gray-600 mb-1">Expiration date</span>
            <input type="date" min={DATE_FLOOR} max={expiryMax()} value={expiry} onChange={(e) => setExpiry(e.target.value)}
              className="bg-white border border-gray-300 rounded-lg px-2 py-1 text-xs" />
          </label>
        )}
      </div>
      <label className="block mt-2">
        <span className="block text-[11px] text-gray-600 mb-1">
          Reason <span className="text-amber-700 font-semibold">(required)</span>
        </span>
        <textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)}
          placeholder="Why is this date changing?"
          className="w-full bg-white border border-gray-300 rounded-lg px-2 py-1 text-xs" />
      </label>
      <div className="flex items-center gap-2 mt-2">
        <button onClick={save} disabled={busy || !changed || !reason.trim()}
          className="bg-amber-600 hover:bg-amber-700 disabled:bg-amber-600/40 text-white text-xs font-semibold px-3 py-1.5 rounded-lg">
          {busy ? 'Saving…' : 'Save correction'}
        </button>
        <button onClick={() => setOpen(false)}
          className="text-xs text-gray-600 hover:text-gray-800 px-2 py-1.5">Cancel</button>
      </div>
    </div>
  );
}

// ─── ASK BEFORE THE CLICK, DO NOT REFUSE AFTER IT ───────────────────
//
// The control stays exactly as it was — a verification still requires an
// expiry traced to the document, and the server still enforces it. What
// changes is WHEN it speaks.
//
// Before: click Verify -> server 400 -> a paragraph explaining that the date
// needs tracing. Measured over 10 days, that refusal fired 43 times on Brenda
// Chamorro alone. A refusal she could not act on in place is a refusal that
// sends her to the workaround, and the workaround is re-uploading as admin.
//
//   A CONTROL THAT REJECTS AFTER THE CLICK TEACHES PEOPLE TO AVOID THE
//   CONTROL. Asking first collects the same evidence and refuses nobody.
//
// So when the row carries an expiry nobody has traced, the date field opens
// FIRST and verification proceeds from what the approver types. Rows with a
// traced expiry, or no expiry at all, verify in one click exactly as before.
function AdminVerifyButton({ userId, item, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [asking, setAsking] = useState(false);
  const [expiry, setExpiry] = useState(dateOnly(item.expiration_date));

  const TRACED = ['document', 'computed_from_standard'];
  // Mirrors resolveDateEdit's gate server-side. The server remains the
  // authority; this only decides whether to ask before sending.
  const needsExpiry =
    (item.requires_expiration_date || item.expiration_date) &&
    !TRACED.includes(item.expiry_provenance);

  const send = async (body) => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post(
        `/file/${userId}/items/${item.source_table}/${item.source_id}/verify`,
        body
      );
      toast.success('Verified');
      setAsking(false);
      onChanged && onChanged();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Verify failed');
    } finally { setBusy(false); }
  };

  if (asking) {
    return (
      <form className="flex flex-wrap items-end gap-2 flex-shrink-0"
        onSubmit={(e) => {
          e.preventDefault();
          if (!expiry) return toast.error('Enter the expiry date printed on the document.');
          send({ expiration_date: expiry });
        }}>
        <label className="block">
          <span className="block text-[10px] font-semibold uppercase text-gray-500 mb-0.5">
            Type the expiry date as printed on this document
          </span>
          <input type="date" min={DATE_FLOOR} max={expiryMax()} value={expiry} onChange={(e) => setExpiry(e.target.value)}
            autoFocus
            className="bg-white border border-gray-300 rounded-lg px-2 py-1 text-xs" />
        </label>
        <button type="submit" disabled={busy}
          className="text-xs font-semibold text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 px-3 py-1.5 rounded-lg">
          {busy ? 'Verifying…' : 'Verify'}
        </button>
        <button type="button" onClick={() => setAsking(false)}
          className="text-xs text-gray-500 px-2 py-1.5">Cancel</button>
      </form>
    );
  }

  return (
    <button onClick={() => (needsExpiry ? setAsking(true) : send({}))} disabled={busy}
      className="text-xs font-semibold text-green-700 hover:text-green-800 inline-flex items-center gap-1 flex-shrink-0 px-3 py-1.5 rounded-lg border border-green-200 hover:bg-green-50 disabled:opacity-50">
      <CheckBadgeIcon className="w-3.5 h-3.5" /> Verify
    </button>
  );
}

// Admin reject opens a small modal that requires a reason. Hits the
// unified reject endpoint which clears the upload + flips status back
// to 'pending' so staff can re-submit.
function AdminRejectButton({ userId, item, onChanged }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button onClick={() => setOpen(true)}
        className="text-xs font-semibold text-red-700 hover:text-red-800 inline-flex items-center gap-1 flex-shrink-0 px-3 py-1.5 rounded-lg border border-red-200 hover:bg-red-50">
        <NoSymbolIcon className="w-3.5 h-3.5" /> Reject
      </button>
      {open && (
        <AdminRejectModal
          userId={userId} item={item}
          onCancel={() => setOpen(false)}
          onChanged={() => { setOpen(false); onChanged && onChanged(); }}
        />
      )}
    </>
  );
}

function AdminRejectModal({ userId, item, onCancel, onChanged }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!reason.trim()) return toast.error('A reason is required.');
    setBusy(true);
    try {
      await api.post(
        `/file/${userId}/items/${item.source_table}/${item.source_id}/reject`,
        { reason: reason.trim() }
      );
      toast.success('Rejected — staff can re-submit');
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Reject failed');
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onCancel}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-base font-semibold text-gray-900 inline-flex items-center gap-2">
              <NoSymbolIcon className="w-5 h-5 text-red-500" />
              Reject "{item.title}"
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Clears the uploaded file and asks the staff member to re-submit.
              The reason is logged and shown on their My Onboarding page.
            </p>
          </div>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="text-[11px] font-semibold uppercase text-gray-500 block mb-1">
              Reason (required)
            </label>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)}
              rows={3} placeholder="e.g. ID is expired, please upload current driver's license"
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm" />
          </div>
          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" onClick={onCancel}
              className="text-sm px-3 py-1.5 rounded-lg text-gray-600 hover:bg-gray-100">
              Cancel
            </button>
            <button type="submit" disabled={busy}
              className="text-sm px-4 py-1.5 rounded-lg bg-red-500 text-white font-semibold hover:bg-red-600 disabled:opacity-50">
              {busy ? 'Rejecting…' : 'Reject + ask for re-submit'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Admin-only per-row upload modal — attaches a doc to a specific
// onboarding step (by template id), records the completion + expiration
// dates, and auto-verifies on submit. Use the existing
// /onboarding/:userId/templates/:templateId/admin-upload endpoint, which
// will find-or-create the progress row and update it.
function AdminRowUploadModal({ userId, item, ownerName, onCancel, onUploaded }) {
  // Whether the upload will auto-verify is decided server-side by the
  // role hierarchy (see src/utils/roleHierarchy.js). The frontend
  // doesn't know the viewer's role here without an extra prop, so we
  // use the safest copy: "and submit for verification." If the
  // server auto-verifies, the toast will read "Uploaded and verified";
  // otherwise it'll read "Uploaded — awaiting verification" via
  // server response. Either is honest.
  const [file, setFile] = useState(null);
  const [notes, setNotes] = useState('');
  // THE COMPLETION DATE COMES OFF THE DOCUMENT OR IT STAYS EMPTY.
  //
  // This field used to default to `new Date().toISOString().slice(0, 10)` —
  // today, in the browser's timezone. Ruled out 2026-09-11 by Ramal Taylor:
  //
  //   "A date that defaults to the upload day is a machine writing a record
  //    and signing a human's name to it."
  //
  // It was not hypothetical. onboarding_progress row 55695 (Lavonn Nance,
  // Child Abuse and Neglect) was filled through this modal on 2026-09-10 and
  // came out wrong on all three date fields — the completion date was the
  // upload day, and the certificate said 2026-06-22. staff_documents row 4 is
  // a second instance through a second path.
  //
  // It was also a UTC-vs-ET bug in its own right: `toISOString()` is UTC, so
  // after 20:00 ET this defaulted to TOMORROW's date. Same class of defect as
  // the CURRENT_DATE sites in staffTimeClockController.
  const [completionDate, setCompletionDate] = useState('');
  const [expirationDate, setExpirationDate] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef();
  // This modal is opened against one known row, so the flag comes straight
  // off the item rather than from a lookup. Same authority either way: the
  // requirement decides whether it stores a completion date.
  const requiresCompletion = !!item?.requires_completion_date;

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error('Pick a file to upload.');
    if (!userId || !item?.template_id) {
      return toast.error('Missing user or step id.');
    }
    const fd = new FormData();
    fd.append('document', file);
    fd.append('template_id', String(item.template_id));
    if (notes)          fd.append('notes', notes);
    if (completionDate) fd.append('completion_date', completionDate);
    if (expirationDate) fd.append('expiration_date', expirationDate);
    setUploading(true);
    try {
      // Unified file endpoint — handles both find-or-create and audit log.
      const { data } = await api.post(
        `/file/${userId}/upload`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      // Server returns the item; if status verified, it auto-verified
      // (uploader had higher tier than target). Otherwise it's
      // 'completed' and waits on someone higher.
      const verified = data?.item?.status === 'verified';
      toast.success(verified ? 'Uploaded and verified' : 'Uploaded — awaiting verification');
      onUploaded();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Upload failed');
    } finally { setUploading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onCancel}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-base font-semibold text-gray-900 inline-flex items-center gap-2">
              <ArrowUpTrayIcon className="w-5 h-5 text-orange-500" />
              Upload "{item.title}"
            </h3>
            {/* WHOSE FILE, IN FULL, WHILE THE FILE IS BEING CHOSEN.
                Two people named Brenda is the roster, not an edge case:
                Brenda Chamorro (director) and Brenda Carrera (float). A
                first name on a picker is how a document lands on the wrong
                person, and ~100 documents in 10 days went through this
                modal. */}
            <p className="text-sm font-semibold text-orange-700 mt-0.5">
              Filing to {ownerName || 'this staff member'}'s file
            </p>
            <p className="text-xs text-gray-500 mt-0.5">
              Uploads the document for this step. If you can verify it
              (you're higher-tier than the file owner), it goes straight
              to Verified; otherwise it lands in your supervisor's
              Pending Approvals queue.
            </p>
          </div>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="text-[11px] font-semibold uppercase text-gray-500 block mb-1">File</label>
            <button type="button" onClick={() => fileInputRef.current?.click()}
              className="w-full text-left rounded-lg border border-dashed border-gray-300 hover:border-orange-400 px-3 py-2 text-sm text-gray-600">
              {file ? `${file.name} (${(file.size / 1024).toFixed(1)} KB)` : 'Click to choose a PDF or image…'}
            </button>
            <input ref={fileInputRef} type="file" className="sr-only"
              accept=".pdf,.jpg,.jpeg,.png,.heic"
              onChange={(e) => setFile(e.target.files?.[0] || null)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            {/* A BOX THAT SHOULD NOT BE THERE GETS FILLED IN. 2026-09-15.
                `requires_completion_date` drove nothing: this field rendered
                on every requirement, including ones whose own description says
                "No dates are needed for this item". Two rows now store the
                UPLOAD DAY as the date a diploma and a CDA were earned, typed
                by reflex into a box the requirement never asked for. The
                server drops such a date now as well — this only stops it being
                offered. */}
            {requiresCompletion && (
            <div>
              <label className="text-[11px] font-semibold uppercase text-gray-500 block mb-1">Completion date</label>
              <input type="date" min={DATE_FLOOR} max={completionMax()} value={completionDate} onChange={(e) => setCompletionDate(e.target.value)}
                className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm" />
              {/* Says what the empty field means, so it is not read as an
                  oversight and filled in with today by reflex. */}
              <p className="text-[11px] text-gray-500 mt-1">
                As printed on the document. Leave blank if it does not say.
              </p>
            </div>
            )}
            <div>
              <label className="text-[11px] font-semibold uppercase text-gray-500 block mb-1">Expires (if any)</label>
              <input type="date" min={DATE_FLOOR} max={expiryMax()} value={expirationDate} onChange={(e) => setExpirationDate(e.target.value)}
                className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm" />
            </div>
          </div>
          <div>
            <label className="text-[11px] font-semibold uppercase text-gray-500 block mb-1">Notes (optional)</label>
            <input value={notes} onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. driver's license, expires 2030"
              className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm" />
          </div>
          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" onClick={onCancel}
              className="text-sm px-3 py-1.5 rounded-lg text-gray-600 hover:bg-gray-100">
              Cancel
            </button>
            <button type="submit" disabled={uploading}
              className="text-sm px-4 py-1.5 rounded-lg bg-orange-500 text-white font-semibold hover:bg-orange-600 disabled:opacity-50">
              {uploading ? 'Uploading…' : 'Upload & verify'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Self-upload modal — staff (or admin uploading for someone else) picks
// a category, attaches a file, optionally pins a completion / expiration
// date, and uploads via POST /api/staff/:id/documents. Server enforces
// the self-vs-other auth rule.
// ─── THE MODAL OFFERS OPEN REQUIREMENTS, NOT CATEGORIES. 2026-09-14 ──
//
// It used to offer a hardcoded list of filing categories — "Training
// certificate", "Compliance document", "Other".
//
//   THE UPLOADER PICKED A FILING-CABINET DRAWER, NOT THE THING THAT WAS
//   ASKED FOR. That is the single biggest source of misfiled documents
//   and it is why 146 files had to be recovered by hand.
//
// It now lists what is actually outstanding for THAT PERSON, by name,
// with why it is open, ordered by urgency. Picking one attaches the
// document to that requirement via template_id.
//
// The category fallback survives for genuine oddities — a document that
// answers no requirement — but it is behind a link, not the default, and
// it asks what the document was meant for. A note is required there for
// the same reason the whole change exists: a document filed under "Other"
// with nothing said about it is the thing somebody has to identify by
// hand months later.
const REASON_LABEL = {
  expired: 'Expired — needs replacing',
  rejected: 'Sent back — needs redoing',
  expiring: 'Expiring soon',
  not_submitted: 'Not yet submitted',
  awaiting_verification: 'Submitted, awaiting verification',
  open: 'Open',
};
const REASON_STYLE = {
  expired: 'bg-red-50 text-red-700 border-red-200',
  rejected: 'bg-amber-50 text-amber-700 border-amber-200',
  expiring: 'bg-amber-50 text-amber-700 border-amber-200',
  not_submitted: 'bg-gray-50 text-gray-600 border-gray-200',
  awaiting_verification: 'bg-blue-50 text-blue-700 border-blue-200',
};

function UploadModal({ targetId, ownerName, onCancel, onUploaded }) {
  const [requirements, setRequirements] = useState([]);
  const [loadingReqs, setLoadingReqs] = useState(true);
  const [templateId, setTemplateId] = useState('');
  const [useCategory, setUseCategory] = useState(false);
  const [category, setCategory] = useState('training_cert');
  const [file, setFile] = useState(null);
  const [notes, setNotes] = useState('');
  const [completionDate, setCompletionDate] = useState('');
  const [expirationDate, setExpirationDate] = useState('');
  const [documentKind, setDocumentKind] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef();

  useEffect(() => {
    let live = true;
    api.get(`/file/${targetId}/open-requirements`)
      .then(({ data }) => { if (live) setRequirements(data); })
      .catch(() => { if (live) setRequirements([]); })
      .finally(() => { if (live) setLoadingReqs(false); });
    return () => { live = false; };
  }, [targetId]);

  const chosen = requirements.find(r => String(r.template_id) === String(templateId));
  // Mirrors the server rule so the field is asked for before the upload is
  // refused — but the SERVER is what enforces it. The client used to be the
  // only check, which is why 104 of 146 recovered files had no date.
  const expiryRequired = !!chosen?.requires_expiration_date;
  // The requirement says whether it stores a completion date. Until a
  // requirement is chosen there is nothing to ask about, so the field stays
  // hidden rather than defaulting to shown.
  const requiresCompletion = !!chosen?.requires_completion_date;
  // 087. Only requirements that distinguish kinds offer the picker.
  const allowedKinds = chosen?.allowed_document_kinds || null;

  // A kind chosen for one requirement must never ride along to another: the
  // database would reject it, but the user would not know why.
  useEffect(() => { setDocumentKind(''); }, [templateId, useCategory]);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error('Pick a file to upload.');
    if (!targetId) return toast.error('No staff target — admin tooling missing.');
    if (!useCategory && !templateId) {
      return toast.error('Pick what this document is for.');
    }
    if (useCategory && !notes.trim()) {
      return toast.error('Say what this document is for — it answers no open requirement.');
    }
    if (expiryRequired && !expirationDate) {
      return toast.error(`"${chosen.step_name}" expires — enter the expiry date printed on it.`);
    }
    const fd = new FormData();
    fd.append('document', file);
    if (useCategory) fd.append('category', category);
    else             fd.append('template_id', templateId);
    if (notes)          fd.append('notes', notes);
    if (documentKind && allowedKinds) fd.append('document_kind', documentKind);
    if (completionDate) fd.append('completion_date', completionDate);
    if (expirationDate) fd.append('expiration_date', expirationDate);
    setUploading(true);
    try {
      // Requirement uploads go through /file/:userId/upload, which files
      // the document AGAINST THE REQUIREMENT. The category path stays on
      // the staff_documents endpoint — it has no requirement to attach to.
      const url = useCategory
        ? `/staff/${targetId}/documents`
        : `/file/${targetId}/upload`;
      await api.post(url, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(useCategory ? 'Uploaded to file' : `Filed against "${chosen.step_name}"`);
      onUploaded();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Upload failed');
    } finally { setUploading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onCancel}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-base font-semibold text-gray-900 inline-flex items-center gap-2">
              <ArrowUpTrayIcon className="w-5 h-5 text-orange-500" />
              Upload to file
            </h3>
            {/* Same rule as the per-row modal: full name, always visible. */}
            <p className="text-sm font-semibold text-orange-700 mt-0.5">
              Filing to {ownerName || 'this staff member'}'s file
            </p>
            <p className="text-xs text-gray-500 mt-0.5">
              Adds a document to the Employment File. Items can't be removed once uploaded — make sure you've got the right file.
            </p>
          </div>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          {/* WHAT IS THIS DOCUMENT FOR — open requirements first. */}
          {!useCategory ? (
            <div>
              <label className="text-[11px] font-semibold uppercase text-gray-500 block mb-1">
                What is this document for?
              </label>
              {loadingReqs ? (
                <p className="text-sm text-gray-500">Loading their open requirements…</p>
              ) : requirements.length === 0 ? (
                <p className="text-sm text-gray-600">
                  Nothing is outstanding for this person.{' '}
                  <button type="button" onClick={() => setUseCategory(true)}
                    className="text-orange-600 hover:underline font-medium">
                    File it to their documents instead
                  </button>.
                </p>
              ) : (
                <>
                  <div className="max-h-56 overflow-y-auto rounded-lg border border-gray-200 divide-y divide-gray-100">
                    {requirements.map(r => (
                      <label key={r.template_id}
                        className={`flex items-start gap-2 px-3 py-2 cursor-pointer hover:bg-orange-50/50 ${String(templateId) === String(r.template_id) ? 'bg-orange-50' : ''}`}>
                        <input type="radio" name="req" className="mt-1"
                          checked={String(templateId) === String(r.template_id)}
                          onChange={() => setTemplateId(r.template_id)} />
                        <span className="min-w-0">
                          <span className="block text-sm font-medium text-gray-900">{r.step_name}</span>
                          <span className={`inline-block mt-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded-full border ${REASON_STYLE[r.reason] || REASON_STYLE.open}`}>
                            {REASON_LABEL[r.reason] || r.reason}
                          </span>
                          {r.requires_expiration_date && (
                            <span className="ml-1 text-[10px] text-gray-500">· expiry required</span>
                          )}
                        </span>
                      </label>
                    ))}
                  </div>
                  <button type="button" onClick={() => setUseCategory(true)}
                    className="mt-2 text-xs text-gray-500 hover:text-orange-600 hover:underline">
                    None of these — file it to their documents instead
                  </button>
                </>
              )}
            </div>
          ) : (
            <div className="rounded-lg border border-amber-200 bg-amber-50/50 p-3">
              <div className="flex items-center justify-between mb-1">
                <label className="text-[11px] font-semibold uppercase text-amber-800">
                  Not a requirement — filing to documents
                </label>
                <button type="button" onClick={() => setUseCategory(false)}
                  className="text-xs text-orange-600 hover:underline">Back to requirements</button>
              </div>
              <select value={category} onChange={(e) => setCategory(e.target.value)}
                className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm bg-white">
                {SELF_UPLOAD_CATEGORIES.map(c => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
              <p className="text-[11px] text-amber-800 mt-1.5">
                Say what it is in the note below — a document filed here answers no
                requirement, and the note is how anyone identifies it later.
              </p>
            </div>
          )}
          <div>
            <label className="text-[11px] font-semibold uppercase text-gray-500 block mb-1">File</label>
            <button type="button" onClick={() => fileInputRef.current?.click()}
              className="w-full text-left rounded-lg border border-dashed border-gray-300 hover:border-orange-400 px-3 py-2 text-sm text-gray-600">
              {file ? `${file.name} (${(file.size / 1024).toFixed(1)} KB)` : 'Click to choose a PDF or image…'}
            </button>
            <input ref={fileInputRef} type="file" className="sr-only"
              accept=".pdf,.jpg,.jpeg,.png,.heic"
              onChange={(e) => setFile(e.target.files?.[0] || null)} />
          </div>
          {/* 087 — WHICH qualifying document is this?
              Shown only for requirements that distinguish kinds. Education is
              ANY-ONE-OF: whichever of these is uploaded satisfies it outright,
              so the copy says so rather than leaving the picker looking like a
              second thing still owed. "Not recorded" is a real, permitted
              answer — six existing rows carry it. */}
          {allowedKinds && allowedKinds.length > 0 && (
            <div className="mb-3">
              <label className="text-[11px] font-semibold uppercase text-gray-500 block mb-1">
                Which document is this?
              </label>
              <select value={documentKind} onChange={(e) => setDocumentKind(e.target.value)}
                className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm bg-white">
                <option value="">Not recorded</option>
                {allowedKinds.map((k) => (
                  <option key={k} value={k}>{kindLabel(k)}</option>
                ))}
              </select>
              <p className="text-[11px] text-gray-500 mt-1">
                Any one of these satisfies "{chosen?.step_name}" on its own.
                Leave it unrecorded if you are not sure — it does not hold the
                requirement open.
              </p>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            {/* Gated on the requirement's own flag, and carrying the same
                guidance AdminRowUploadModal has had since 2026-09-11. This
                modal never had it, which is the half of the defect that let
                today's date look like the expected answer. */}
            {requiresCompletion && (
            <div>
              <label className="text-[11px] font-semibold uppercase text-gray-500 block mb-1">Completion date</label>
              <input type="date" min={DATE_FLOOR} max={completionMax()} value={completionDate} onChange={(e) => setCompletionDate(e.target.value)}
                className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm" />
              <p className="text-[11px] text-gray-500 mt-1">
                As printed on the document. Leave blank if it does not say.
              </p>
            </div>
            )}
            <div>
              <label className={`text-[11px] font-semibold uppercase block mb-1 ${expiryRequired ? 'text-red-600' : 'text-gray-500'}`}>
                {expiryRequired ? 'Expires — REQUIRED' : 'Expires (if any)'}
              </label>
              <input type="date" min={DATE_FLOOR} max={expiryMax()} value={expirationDate} onChange={(e) => setExpirationDate(e.target.value)}
                className={`w-full rounded-lg border px-3 py-1.5 text-sm ${expiryRequired && !expirationDate ? 'border-red-300 bg-red-50' : 'border-gray-200'}`} />
              {expiryRequired && (
                <p className="text-[11px] text-red-600 mt-1">
                  Enter the date printed on the document. The server refuses this
                  upload without it.
                </p>
              )}
            </div>
          </div>
          <div>
            <label className="text-[11px] font-semibold uppercase text-gray-500 block mb-1">Notes (optional)</label>
            <input value={notes} onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. 2026 CDC certificate"
              className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm" />
          </div>
          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" onClick={onCancel}
              className="text-sm px-3 py-1.5 rounded-lg text-gray-600 hover:bg-gray-100">
              Cancel
            </button>
            <button type="submit" disabled={uploading}
              className="text-sm px-4 py-1.5 rounded-lg bg-orange-500 text-white font-semibold hover:bg-orange-600 disabled:opacity-50">
              {uploading ? 'Uploading…' : 'Upload'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
